/**
 * v1 mock subsystem registry. When Phase 2 lands the real accounting-transition
 * subsystem, this file becomes the swap point — the pages consume the registry
 * shape, not the hardcoded pair.
 *
 * Scope / condition mirror the include+exclude nested structure exposed by
 * @spendable-tech/workflow-primitives@2.0.0. A workflow fires when
 *   (include empty OR matches) AND (exclude empty OR doesn't match)
 * applied independently to scope and condition.
 */

import { MINUTES_PER_DAY, cardStatusMapping, cardTypeMapping, walletTypeMapping } from "src/components/constant/constant";
import {
  TREE_FIELD_TO_STATUS,
  TREE_KEY_DEFAULTS_ALLOCATION,
  TREE_KEY_NOTIFICATION_ACCOUNTING,
  TREE_KEY_NOTIFICATION_REIMBURSEMENT,
  TREE_KEY_NOTIFICATION_TRANSACTION,
  TREE_KEY_NOTIFICATION_WALLET,
  TREE_KEY_SCHEDULER_ACCOUNTING,
  TREE_KEY_SCHEDULER_CARD,
  TREE_KEY_SCHEDULER_CARD_SETTING,
  TREE_KEY_TRANSITION_ACCOUNTING,
  conditionTreeFieldDef,
  getConditionTreeVocabulary,
  isConditionTreeGroupShape,
  splitConditionTreeAnchor
} from "src/components/workflowComponent/conditionTreeBuilder/conditionTreeModel";
import type {
  ConditionTreeGroupShape,
  ConditionTreeGroupType,
  ConditionTreeLeafShape,
  ConditionTreeNodeShape,
  ConditionTreeOp
} from "src/components/workflowComponent/conditionTreeBuilder/conditionTreeModel";
import { formatSydneyDay } from "src/utils";

export const SUBSYSTEM_TRANSITION = "TRANSITION";
export const SUBSYSTEM_NOTIFICATION = "NOTIFICATION";
export const SUBSYSTEM_SCHEDULER = "SCHEDULER";
export const SUBSYSTEM_DEFAULTS = "DEFAULTS";
export const ENTITY_TYPE_ACCOUNTING = "ACCOUNTING";
export const ENTITY_TYPE_REIMBURSEMENT = "REIMBURSEMENT";
export const ENTITY_TYPE_NOTIFICATION = "NOTIFICATION";
export const ENTITY_TYPE_TRANSACTION = "TRANSACTION";
export const ENTITY_TYPE_WALLET = "WALLET";
export const ENTITY_TYPE_ALLOCATION = "ALLOCATION";
export const ENTITY_TYPE_CARD = "CARD";
export const ENTITY_TYPE_CARD_SETTING = "CARD_SETTING";
// Create-time priority for rules that don't compete (notification, scheduler)
// and for templates; competing org rules are placed after their group's last rule.
export const DEFAULT_WORKFLOW_PRIORITY = 100;

export const ACTION_AUTO_TRANSITION = "AUTO_TRANSITION";
export const ACTION_BLOCK_TRANSITION = "BLOCK_TRANSITION";
export const ACTION_SEND_NOTIFICATION = "SEND_NOTIFICATION";
export const ACTION_EQUAL_PARTICIPANT_SPLIT = "EQUAL_PARTICIPANT_SPLIT";
export const ACTION_DEACTIVATE_CARD_SETTINGS = "DEACTIVATE_CARD_SETTINGS";

// One name per condition field. Every surface that names a condition field —
// builder rows, previews, list summaries, the detail page and the legacy flat
// forms — reads it from here.
export const CONDITION_FIELD_LABELS = {
  RECORD_TYPE: "Record type",
  WALLET: "Wallet",
  AMOUNT: "Amount",
  CLAIM_AMOUNT: "Claim amount",
  AMOUNT_MOVED: "Amount moved",
  PAYMENT_AMOUNT: "Payment amount",
  RECEIPT: "Receipt",
  RECEIPT_COUNT: "Number of receipts",
  NOTE: "Note",
  WALLET_APPROVERS: "Wallet approvers",
  WALLET_VERIFIERS: "Wallet verifiers",
  WHAT_HAPPENED: "What happened",
  FROM_STATUS: "Moved from status",
  STATUS: "Status",
  STATUS_AFTER: "Status afterwards",
  CLAIM_STATUS_AFTER: "Claim status afterwards",
  WHO_SPENT: "Who spent the money",
  WHO_SUBMITTED_CLAIM: "Who submitted the claim",
  CARDHOLDER: "Cardholder",
  PAID_FROM_WALLET: "Paid from wallet",
  PAID_INTO_WALLET: "Paid into wallet",
  MERCHANT_NAME: "Merchant name",
  MERCHANT_CATEGORY: "Merchant category",
  MCC: "Merchant category code (MCC)",
  PAYMENT_OUTCOME: "Payment outcome",
  DECLINE_REASON: "Decline reason",
  DAY_OF_WEEK: "Day of the week",
  BALANCE_AFTER: "Balance afterwards",
  BALANCE_BEFORE: "Balance before",
  MOVEMENT: "Kind of movement",
  WALLET_TYPE: "Wallet type",
  CARD: "Card",
  CARD_STATUS: "Card status",
  CARD_TYPE: "Card type",
  CONNECTION_TYPE: "Connection type",
  TIME_OF_DAY: "Time of day"
} as const;

// The time-of-day row's only check — the window is stored as a minute_of_day pair.
export const TIME_OF_DAY_CHECK = "is between";

// What the rule preview says for something not picked yet — never a choice nobody made.
export const PREVIEW_UNSET_COPY = {
  NOBODY: "nobody selected yet",
  NO_WALLET: "no wallet chosen yet",
  NOT_CHOSEN: "is not chosen yet",
  NOTHING_HAPPENS: "nothing happens yet",
  AN_AMOUNT: "an amount"
} as const;

// Boolean fields carry their meaning in the check: [true, false].
export const CONDITION_FIELD_CHECKS = {
  RECEIPT: ["is attached", "is missing"],
  NOTE: ["has a note", "has no note"],
  WALLET_APPROVERS: ["are set up", "are not set up"],
  WALLET_VERIFIERS: ["are set up", "are not set up"]
} as const;

export const conditionCheckLabel = (checks: readonly [string, string], value: boolean): string =>
  value ? checks[0] : checks[1];

export interface NotificationTemplateOption {
  value: string;
  label: string;
  // Set when the entity's events are outcomes rather than transitions — the
  // form derives the template from the picked outcome instead of a dropdown.
  outcome?: string;
}

export interface NotificationEntityConfig {
  object_types: string[];
  ops: string[];
  statuses: string[];
  templates: NotificationTemplateOption[];
}

// Per-entity-type option lists for the NOTIFICATION subsystem form. Ops and
// statuses mirror each producer's backend enums (ACCOUNTING: transaction-service
// TransitionEvent.op; REIMBURSEMENT: @spendable-tech/dynamodb ReimbursementAuditOp
// + ReimbursementStatus; TRANSACTION: notify-service card auth events).
// TODO: source from the workflow-primitives registry once it's exposed
// via a client-side helper.
export const NOTIFICATION_ENTITY_CONFIG: Record<string, NotificationEntityConfig> = {
  [ENTITY_TYPE_ACCOUNTING]: {
    object_types: ["ACCOUNTING"],
    ops: ["CREATE", "SUBMIT", "APPROVE", "MARK_READY", "REJECT", "RESUBMIT", "MARK_DRAFT"],
    statuses: ["DRAFT", "SUBMITTED", "APPROVED", "READY", "REJECTED"],
    templates: [
      { value: "accounting_status_changed", label: "Accounting record moved to a new status" },
      { value: "accounting_submitted", label: "Transaction pending approval" },
      { value: "accounting_receipt_required", label: "Receipt upload required (record created in draft)" }
    ]
  },
  [ENTITY_TYPE_REIMBURSEMENT]: {
    object_types: ["REIMBURSEMENT"],
    // CREATE is a valid ReimbursementAuditOp but creation never publishes a
    // workflow event (audit row is written transactionally) — a CREATE rule
    // can never fire, so it is not offered. Stored CREATE rules still render.
    ops: [
      "UPDATE",
      "SUBMIT",
      "RESUBMIT",
      "APPROVE",
      "REJECT",
      "CANCEL",
      "RECALL",
      "SETTLEMENT_STARTED",
      "SETTLE",
      "SETTLEMENT_FAILED"
    ],
    statuses: ["DRAFT", "SUBMITTED", "APPROVED", "REJECTED", "CANCELLED", "SETTLED"],
    templates: [{ value: "reimbursement_status_changed", label: "Reimbursement status changed" }]
  },
  [ENTITY_TYPE_TRANSACTION]: {
    object_types: ["TRANSACTION"],
    ops: ["AUTHORIZE"],
    statuses: ["APPROVED", "DECLINED"],
    templates: [
      { value: "card_authorized_notification", label: "Card authorized (receipt)", outcome: "APPROVED" },
      { value: "card_declined_notification", label: "Card declined", outcome: "DECLINED" }
    ]
  },
  [ENTITY_TYPE_WALLET]: {
    object_types: ["WALLET"],
    ops: ["DEPOSIT", "ADJUSTMENT", "TRANSFER_IN", "TRANSFER_OUT", "PURCHASE", "WITHDRAWAL", "CASH"],
    // WALLET producers hardcode from_status "" / to_status "SETTLED" — a
    // constant, not a vocabulary — so status filters are meaningless here.
    statuses: [],
    templates: [{ value: "wallet_balance_alert", label: "Wallet balance alert" }]
  }
};

export const getNotificationEntityConfig = (entityType?: string | null): NotificationEntityConfig =>
  NOTIFICATION_ENTITY_CONFIG[entityType ?? ENTITY_TYPE_ACCOUNTING] ??
  NOTIFICATION_ENTITY_CONFIG[ENTITY_TYPE_ACCOUNTING];

// ACCOUNTING NOTIFICATION rules pick the template from the from_state -> to_state
// pair the user selected: DRAFT -> SUBMITTED is a call-to-action for approvers
// (accounting_submitted); every other transition is the informational default
// (accounting_status_changed). The FE stores the derived value verbatim into
// action_params.template; notify-service reads it back unchanged.
export const ACCOUNTING_TEMPLATE_BY_TRANSITION: Record<string, string> = {
  "DRAFT->SUBMITTED": "accounting_submitted"
};

export const ACCOUNTING_DEFAULT_TEMPLATE = "accounting_status_changed";

export const resolveAccountingTemplate = (from_state?: string, to_state?: string): string => {
  if (!from_state || !to_state) return ACCOUNTING_DEFAULT_TEMPLATE;
  return ACCOUNTING_TEMPLATE_BY_TRANSITION[`${from_state}->${to_state}`] ?? ACCOUNTING_DEFAULT_TEMPLATE;
};

// Op-level mapping for the derived template (Q13): CREATE is the
// record-created-in-draft trigger (receipt call-to-action), SUBMIT is the
// DRAFT->SUBMITTED op. Everything else falls back to the informational default.
export const ACCOUNTING_TEMPLATE_BY_OP: Record<string, string> = {
  CREATE: "accounting_receipt_required",
  SUBMIT: "accounting_submitted"
};

// Single source for the derived ACCOUNTING template. Call-to-action copy only
// ships when the trigger is unambiguous (one op, or one from->to pair);
// multi-trigger rules get the default so informational matches never carry it.
export const resolveAccountingTemplateForTrigger = (
  ops: string[],
  from_status: string[],
  to_status: string[]
): string => {
  if (ops.length === 1 && ACCOUNTING_TEMPLATE_BY_OP[ops[0]]) return ACCOUNTING_TEMPLATE_BY_OP[ops[0]];
  if (ops.length > 1) return ACCOUNTING_DEFAULT_TEMPLATE;
  if (from_status.length === 1 && to_status.length === 1) {
    return resolveAccountingTemplate(from_status[0], to_status[0]);
  }
  return ACCOUNTING_DEFAULT_TEMPLATE;
};

// Object-types default: when the entity registers exactly one object type,
// pre-select it so the user never opens a one-item dropdown. Multi-option
// entities (none today) return [] and the user picks.
export const getDefaultNotificationObjectTypes = (entityType?: string | null): string[] => {
  const options = getNotificationEntityConfig(entityType).object_types;

  return options.length === 1 ? [...options] : [];
};

// Template default mirrors the object-types rule: a single template is
// pre-selected. Outcome-tagged templates are excluded — outcome-driven
// entities (TRANSACTION) derive the template from the picked outcome instead.
// ACCOUNTING derives its default from the transition mapping so a fresh rule
// (empty from/to) starts on accounting_status_changed even though the entity
// now has more than one template registered.
export const getDefaultNotificationTemplate = (entityType?: string | null): string => {
  if (entityType === ENTITY_TYPE_ACCOUNTING) return resolveAccountingTemplate();
  const options = getNotificationEntityConfig(entityType).templates;

  return options.length === 1 && !options[0].outcome ? options[0].value : "";
};

export const NOTIFICATION_OUTCOME_DECLINED = "DECLINED";

// TRANSACTION events are outcomes, not transitions — from_status is always
// empty on the wire and the producer writes the outcome into to_status.
export const isOutcomeDrivenNotificationEntity = (entityType?: string | null): boolean =>
  getNotificationEntityConfig(entityType).templates.some((t) => Boolean(t.outcome));

// Entities with no status vocabulary (WALLET: producers emit constants) — the
// form hides the from/to status controls and payloads never carry status keys.
export const isStatuslessNotificationEntity = (entityType?: string | null): boolean =>
  getNotificationEntityConfig(entityType).statuses.length === 0;

const OUTCOME_LABELS: Record<string, string> = {
  APPROVED: "Approved",
  DECLINED: "Declined"
};

export const notificationOutcomeLabel = (value?: string | null): string =>
  value ? OUTCOME_LABELS[value] ?? value : "";

export interface NotificationOutcomeOption {
  value: string;
  label: string;
}

export const getOutcomeOptionsForEntity = (entityType?: string | null): NotificationOutcomeOption[] => {
  const byOutcome = new Map<string, NotificationOutcomeOption>();

  getNotificationEntityConfig(entityType).templates.forEach((t) => {
    if (t.outcome && !byOutcome.has(t.outcome)) {
      byOutcome.set(t.outcome, { value: t.outcome, label: OUTCOME_LABELS[t.outcome] ?? t.outcome });
    }
  });
  return Array.from(byOutcome.values());
};

export const getTemplateForOutcome = (
  entityType: string | null | undefined,
  outcome?: string | null
): string => {
  if (!outcome) return "";
  return getNotificationEntityConfig(entityType).templates.find((t) => t.outcome === outcome)?.value ?? "";
};

// SCHEDULER rules are evaluated by a cron against a windowed query, not by an
// event — so there is no `ops` key here. Statuses mirror the NOTIFICATION
// entry for the same entity (same producer enum).
// condition_kind picks the wire shape the entity's .strict() backend schema
// accepts: AGE_WINDOW = { age_hours {min,max}, statuses?, has_receipt? };
// EXPIRY_THRESHOLD = { expires_within_days } (re-fires daily until expiry);
// SETTING_FILTER = the CARD_SETTING candidate query — every field optional,
// empty = all active card settings in the org.
export type SchedulerConditionKind = "AGE_WINDOW" | "EXPIRY_THRESHOLD" | "SETTING_FILTER";

export interface SchedulerEntityConfig {
  object_types: string[];
  statuses: string[];
  templates: NotificationTemplateOption[];
  condition_kind: SchedulerConditionKind;
}

export const SCHEDULER_ENTITY_CONFIG: Record<string, SchedulerEntityConfig> = {
  [ENTITY_TYPE_ACCOUNTING]: {
    object_types: ["ACCOUNTING"],
    statuses: ["DRAFT", "SUBMITTED", "APPROVED", "READY", "REJECTED"],
    templates: [{ value: "receipt_missing_reminder", label: "Receipt missing reminder" }],
    condition_kind: "AGE_WINDOW"
  },
  [ENTITY_TYPE_CARD]: {
    object_types: ["CARD"],
    statuses: [],
    templates: [{ value: "card_expiring_soon", label: "Card expiring soon (owner notice)" }],
    condition_kind: "EXPIRY_THRESHOLD"
  },
  // DEACTIVATE_CARD_SETTINGS carries no template/recipients — the action is
  // the whole Then. Object type is auto-filled; the raw enum never renders.
  [ENTITY_TYPE_CARD_SETTING]: {
    object_types: ["CARD_SETTING"],
    statuses: [],
    templates: [],
    condition_kind: "SETTING_FILTER"
  }
};

export const getSchedulerEntityConfig = (entityType?: string | null): SchedulerEntityConfig =>
  SCHEDULER_ENTITY_CONFIG[entityType ?? ENTITY_TYPE_ACCOUNTING] ??
  SCHEDULER_ENTITY_CONFIG[ENTITY_TYPE_ACCOUNTING];

// SCHEDULER registers exactly one template per notify entity — derived, never
// picked (CARD_SETTING has none; the action is the whole Then).
export const getDefaultSchedulerTemplate = (entityType?: string | null): string =>
  getSchedulerEntityConfig(entityType).templates[0]?.value ?? "";

export const isExpiryThresholdSchedulerEntity = (entityType?: string | null): boolean =>
  getSchedulerEntityConfig(entityType).condition_kind === "EXPIRY_THRESHOLD";

export const isCardSettingSchedulerEntity = (entityType?: string | null): boolean =>
  entityType === ENTITY_TYPE_CARD_SETTING;

// SCHEDULER registers exactly one action per entity — notify entities send,
// CARD_SETTING deactivates. The form derives it on entity switch instead of
// offering a picker (same fixed-action pattern as DEFAULTS).
export const schedulerActionForEntity = (entityType?: string | null): string =>
  isCardSettingSchedulerEntity(entityType) ? ACTION_DEACTIVATE_CARD_SETTINGS : ACTION_SEND_NOTIFICATION;

// ─── Label layer (Q16) — every workflow surface routes enum display through
// these maps; enumToPhrase is the single sanctioned fallback for free-text
// values (e.g. stored relationship types). No inline humanisation elsewhere.

export const enumToPhrase = (value: string): string => value.replace(/_/g, " ").toLowerCase();

// Event-op vocabulary across all notification/transition producers.
export const WORKFLOW_OP_LABELS: Record<string, string> = {
  CREATE: "Created",
  UPDATE: "Updated",
  SUBMIT: "Submitted",
  RESUBMIT: "Resubmitted",
  APPROVE: "Approved",
  REJECT: "Rejected",
  MARK_READY: "Marked ready",
  MARK_DRAFT: "Marked draft",
  CANCEL: "Cancelled",
  RECALL: "Recalled",
  SETTLEMENT_STARTED: "Settlement started",
  SETTLE: "Settled",
  SETTLEMENT_FAILED: "Settlement failed",
  AUTHORIZE: "Authorized",
  DEPOSIT: "Deposit",
  ADJUSTMENT: "Adjustment",
  TRANSFER_IN: "Transfer in",
  TRANSFER_OUT: "Transfer out",
  PURCHASE: "Purchase",
  WITHDRAWAL: "Withdrawal",
  CASH: "Cash"
};

export const workflowOpLabel = (value?: string | null): string =>
  value ? WORKFLOW_OP_LABELS[value] ?? value : "";

// Status vocabulary across ACCOUNTING + REIMBURSEMENT + TRANSACTION outcomes.
export const WORKFLOW_STATUS_LABELS: Record<string, string> = {
  DRAFT: "Draft",
  SUBMITTED: "Submitted",
  APPROVED: "Approved",
  READY: "Ready",
  REJECTED: "Rejected",
  CANCELLED: "Cancelled",
  SETTLED: "Settled",
  DECLINED: "Declined"
};

export const workflowStatusLabel = (value?: string | null): string =>
  value ? WORKFLOW_STATUS_LABELS[value] ?? value : "";

export const ACTIVITY_NAME_LABELS: Record<string, string> = {
  CASH_TRANSACTION: "Cash transaction",
  NOVATTI_CARD_TRANSACTION: "Card transaction",
  WALLET_TRANSACTION: "Wallet transaction"
};

export const activityNameLabel = (value?: string | null): string =>
  value ? ACTIVITY_NAME_LABELS[value] ?? value : "";

// relationship_based source objects — wider than entity types (USER/ORGANISATION).
export const SOURCE_OBJECT_TYPE_LABELS: Record<string, string> = {
  WALLET: "Wallet",
  USER: "User",
  ORGANISATION: "Organisation"
};

export const sourceObjectTypeLabel = (value?: string | null): string =>
  value ? SOURCE_OBJECT_TYPE_LABELS[value] ?? value : "";

export const templateLabel = (slug?: string | null): string => {
  if (!slug) return "";
  const configs = [...Object.values(NOTIFICATION_ENTITY_CONFIG), ...Object.values(SCHEDULER_ENTITY_CONFIG)];

  for (const config of configs) {
    const match = config.templates.find((t) => t.value === slug);

    if (match) return match.label;
  }
  return slug;
};

// Canonical activity_name values from @spendable-tech/activity/src/define/.
// v1 mock — will move to the workflow-primitives registry alongside states.
export const ACCOUNTING_ACTIVITY_NAMES = [
  "CASH_TRANSACTION",
  "NOVATTI_CARD_TRANSACTION",
  "WALLET_TRANSACTION"
] as const;

// Mirrors @spendable-tech/dynamodb ACCOUNTING_TRANSACTION_TRANSITIONS.
// Backend validates via workflow-primitives valid_transitions; this mock keeps
// the FE `to_state` picker in sync so users only see valid destinations.
export const ACCOUNTING_TRANSITIONS = [
  ["DRAFT", "SUBMITTED"],
  ["DRAFT", "READY"],
  ["SUBMITTED", "APPROVED"],
  ["SUBMITTED", "REJECTED"],
  ["SUBMITTED", "READY"],
  ["APPROVED", "READY"],
  ["APPROVED", "REJECTED"],
  ["READY", "DRAFT"],
  ["REJECTED", "SUBMITTED"]
] as const;

export interface WorkflowScopeBlock {
  wallet_ids?: string[];
}

export interface WorkflowScopeShape {
  include?: WorkflowScopeBlock;
  exclude?: WorkflowScopeBlock;
}

export interface WorkflowConditionBlock {
  has_receipt?: boolean;
  min_receipt_count?: number;
  min_amount?: number;
  activity_names?: string[];
  has_approvers?: boolean;
  has_verifiers?: boolean;
}

export interface WorkflowConditionShape {
  include?: WorkflowConditionBlock;
  exclude?: WorkflowConditionBlock;
}

// Mirrors accountingTransitionActionParamsSchema in @spendable-tech/workflow-primitives.
// AUTO_TRANSITION carries `target_state`; BLOCK_TRANSITION carries `message`
// instead. The backend refuses a payload that sets both.
export interface WorkflowActionParamsShape {
  target_state?: string;
  message?: string;
  note?: string;
}

export interface SubsystemOption {
  value: string;
  label: string;
}

export interface EntityTypeOption {
  value: string;
  label: string;
}

export interface WorkflowActionOption {
  value: string;
  label: string;
}

export interface SubsystemPair {
  subsystem: string;
  entity_type: string;
  states: string[];
  scope_fields: readonly (keyof WorkflowScopeBlock)[];
  condition_fields: readonly (keyof WorkflowConditionBlock)[];
  activity_names: readonly string[];
  valid_transitions?: readonly (readonly [string, string])[];
}

export const SUBSYSTEM_OPTIONS: SubsystemOption[] = [
  { value: SUBSYSTEM_TRANSITION, label: "Transition" },
  { value: SUBSYSTEM_NOTIFICATION, label: "Notification" },
  { value: SUBSYSTEM_SCHEDULER, label: "Scheduler" },
  { value: SUBSYSTEM_DEFAULTS, label: "Defaults" }
];

export const ENTITY_TYPE_OPTIONS: EntityTypeOption[] = [
  { value: ENTITY_TYPE_ACCOUNTING, label: "Accounting" }
];

// v1: NOTIFICATION subsystem registers a single (NOTIFICATION, ACCOUNTING) pair
// in workflow-primitives. As REIMBURSEMENT / WALLET / USER / ASSET producers
// land, extend this array — TODO: source from the workflow-primitives registry
// once it's exposed via a client-side helper.
export const NOTIFICATION_ENTITY_TYPE_OPTIONS: EntityTypeOption[] = [
  { value: ENTITY_TYPE_ACCOUNTING, label: "Accounting" },
  { value: ENTITY_TYPE_REIMBURSEMENT, label: "Reimbursement" },
  { value: ENTITY_TYPE_TRANSACTION, label: "Card transaction" },
  { value: ENTITY_TYPE_WALLET, label: "Wallet" }
];

// SCHEDULER pairs registered in workflow-primitives (3.10.0): ACCOUNTING +
// CARD (notify) and CARD_SETTING (deactivate). Extend as the other scheduled
// scenarios (digests) land.
export const SCHEDULER_ENTITY_TYPE_OPTIONS: EntityTypeOption[] = [
  { value: ENTITY_TYPE_ACCOUNTING, label: "Accounting" },
  { value: ENTITY_TYPE_CARD, label: "Card" },
  { value: ENTITY_TYPE_CARD_SETTING, label: "Card connection" }
];

// v1: DEFAULTS registers (DEFAULTS, ALLOCATION) only —
// matches workflow-primitives 3.2.0 registry.
export const DEFAULTS_ENTITY_TYPE_OPTIONS: EntityTypeOption[] = [
  { value: ENTITY_TYPE_ALLOCATION, label: "Allocation" }
];

// Union of every registered pair's entity options — the list filter spans all
// subsystems, so it offers the full vocabulary (deduped on value).
export const WORKFLOW_FILTER_ENTITY_TYPE_OPTIONS: EntityTypeOption[] = [
  ...ENTITY_TYPE_OPTIONS,
  ...NOTIFICATION_ENTITY_TYPE_OPTIONS,
  ...SCHEDULER_ENTITY_TYPE_OPTIONS,
  ...DEFAULTS_ENTITY_TYPE_OPTIONS
].filter((option, index, all) => all.findIndex((o) => o.value === option.value) === index);

export const WORKFLOW_ACTION_OPTIONS: WorkflowActionOption[] = [
  { value: ACTION_AUTO_TRANSITION, label: "Auto transition" },
  { value: ACTION_BLOCK_TRANSITION, label: "Block transition" },
  { value: ACTION_SEND_NOTIFICATION, label: "Send notification" },
  { value: ACTION_EQUAL_PARTICIPANT_SPLIT, label: "Equal participant split" },
  { value: ACTION_DEACTIVATE_CARD_SETTINGS, label: "Switch off card connections" }
];

// Plain-English rule types the builder presents; subsystem + action are
// derived silently and stored exactly as before.
export interface WorkflowRuleTypeOption {
  value: string;
  label: string;
  subsystem: string;
  action: string;
}

export const WORKFLOW_RULE_TYPE_OPTIONS: WorkflowRuleTypeOption[] = [
  {
    value: "AUTO_CHANGE_STATUS",
    label: "Automatically change status",
    subsystem: SUBSYSTEM_TRANSITION,
    action: ACTION_AUTO_TRANSITION
  },
  {
    value: "BLOCK_STATUS_CHANGE",
    label: "Block a status change",
    subsystem: SUBSYSTEM_TRANSITION,
    action: ACTION_BLOCK_TRANSITION
  },
  {
    value: "SEND_NOTIFICATION",
    label: "Send a notification",
    subsystem: SUBSYSTEM_NOTIFICATION,
    action: ACTION_SEND_NOTIFICATION
  },
  {
    // Covers both scheduled sends and the CARD_SETTING cutoff — the actual
    // action is derived from the picked entity (schedulerActionForEntity).
    value: "SCHEDULED_NOTIFICATION",
    label: "Run on a schedule",
    subsystem: SUBSYSTEM_SCHEDULER,
    action: ACTION_SEND_NOTIFICATION
  },
  {
    value: "AUTO_SPLIT",
    label: "Split transactions automatically",
    subsystem: SUBSYSTEM_DEFAULTS,
    action: ACTION_EQUAL_PARTICIPANT_SPLIT
  }
];

// TRANSITION differentiates by action (auto vs block); the other subsystems
// have exactly one rule type each.
export const ruleTypeOptionFor = (
  subsystem?: string | null,
  action?: string | null
): WorkflowRuleTypeOption | undefined =>
  WORKFLOW_RULE_TYPE_OPTIONS.find(
    (o) => o.subsystem === subsystem && (o.subsystem !== SUBSYSTEM_TRANSITION || o.action === action)
  );

// v1 mock — supported (subsystem, entity_type) pairs with their state lists.
// Source of truth for TRANSITION: transaction-service/src/helpers/validation/schema.ts.
// Source of truth for NOTIFICATION: @spendable-tech/workflow-primitives
// notification/schemas.ts.
export const SUBSYSTEM_PAIRS: SubsystemPair[] = [
  {
    subsystem: SUBSYSTEM_TRANSITION,
    entity_type: ENTITY_TYPE_ACCOUNTING,
    states: ["DRAFT", "SUBMITTED", "APPROVED", "READY", "REJECTED"],
    scope_fields: ["wallet_ids"],
    condition_fields: [
      "has_receipt",
      "min_receipt_count",
      "min_amount",
      "activity_names",
      "has_approvers",
      "has_verifiers"
    ],
    activity_names: ACCOUNTING_ACTIVITY_NAMES,
    valid_transitions: ACCOUNTING_TRANSITIONS
  },
  {
    subsystem: SUBSYSTEM_NOTIFICATION,
    entity_type: ENTITY_TYPE_ACCOUNTING,
    states: [],
    scope_fields: [],
    condition_fields: [],
    activity_names: []
  },
  {
    subsystem: SUBSYSTEM_SCHEDULER,
    entity_type: ENTITY_TYPE_ACCOUNTING,
    states: [],
    scope_fields: [],
    condition_fields: [],
    activity_names: []
  },
  {
    subsystem: SUBSYSTEM_SCHEDULER,
    entity_type: ENTITY_TYPE_CARD,
    states: [],
    scope_fields: [],
    condition_fields: [],
    activity_names: []
  },
  {
    subsystem: SUBSYSTEM_SCHEDULER,
    entity_type: ENTITY_TYPE_CARD_SETTING,
    states: [],
    scope_fields: [],
    condition_fields: [],
    activity_names: []
  },
  {
    subsystem: SUBSYSTEM_DEFAULTS,
    entity_type: ENTITY_TYPE_ALLOCATION,
    states: [],
    scope_fields: [],
    condition_fields: [],
    activity_names: []
  }
];

const getPair = (subsystem?: string | null, entity_type?: string | null): SubsystemPair | undefined => {
  if (!subsystem || !entity_type) return undefined;
  return SUBSYSTEM_PAIRS.find((p) => p.subsystem === subsystem && p.entity_type === entity_type);
};

export const getStatesForPair = (subsystem?: string | null, entity_type?: string | null): string[] => {
  return getPair(subsystem, entity_type)?.states ?? [];
};

export const getScopeFieldsForPair = (
  subsystem?: string | null,
  entity_type?: string | null
): readonly (keyof WorkflowScopeBlock)[] => {
  return getPair(subsystem, entity_type)?.scope_fields ?? [];
};

export const getConditionFieldsForPair = (
  subsystem?: string | null,
  entity_type?: string | null
): readonly (keyof WorkflowConditionBlock)[] => {
  return getPair(subsystem, entity_type)?.condition_fields ?? [];
};

export const getActivityNamesForPair = (
  subsystem?: string | null,
  entity_type?: string | null
): readonly string[] => {
  return getPair(subsystem, entity_type)?.activity_names ?? [];
};

// Returns valid `to_state` options for the given `from_state`. Falls back to
// the pair's full state list when a pair defines no `valid_transitions` (so
// unregistered subsystems keep the pre-filter behaviour).
export function getValidToStatesForPair(
  subsystem: string,
  entity_type: string,
  from_state: string
): string[] {
  const pair = getPair(subsystem, entity_type);

  if (!pair || !from_state) return getStatesForPair(subsystem, entity_type);
  if (!pair.valid_transitions) return [...pair.states];
  const toStates = pair.valid_transitions.filter(([from]) => from === from_state).map(([, to]) => to);

  return toStates.length > 0 ? toStates : [...pair.states];
}

export const subsystemLabel = (value?: string | null): string => {
  if (!value) return "";
  const match = SUBSYSTEM_OPTIONS.find((o) => o.value === value);

  return match?.label ?? value;
};

// Q12 filter language — customer phrasing for the list filter headings.
// Stored subsystem/entity values and queryConstants keys are untouched.
export const FILTER_WHAT_IT_DOES_LABEL = "What it does";
export const FILTER_WHAT_ITS_ABOUT_LABEL = "What it's about";

// "What it does" phrasing per subsystem (Q12) — list filter options and
// competing-cluster trigger headings.
export const SUBSYSTEM_PLAIN_LABELS: Record<string, string> = {
  [SUBSYSTEM_TRANSITION]: "Moves it along",
  [SUBSYSTEM_NOTIFICATION]: "Tells someone",
  [SUBSYSTEM_SCHEDULER]: "Checks daily",
  [SUBSYSTEM_DEFAULTS]: "Splits the cost"
};

export const subsystemPlainLabel = (value?: string | null): string =>
  value ? SUBSYSTEM_PLAIN_LABELS[value] ?? subsystemLabel(value) : "";

// What happens when nothing in a competing cluster matches (Q12) — one muted
// line per cluster in the grouped list.
export const SUBSYSTEM_FALLBACK_COPY: Record<string, string> = {
  [SUBSYSTEM_TRANSITION]: "If none of these match, the change is allowed as normal.",
  [SUBSYSTEM_NOTIFICATION]: "If none match, no one is notified.",
  [SUBSYSTEM_SCHEDULER]: "If none match, the daily check does nothing.",
  [SUBSYSTEM_DEFAULTS]: "If none match, the cost is not split automatically."
};

export const subsystemFallbackCopy = (value?: string | null): string =>
  value ? SUBSYSTEM_FALLBACK_COPY[value] ?? "" : "";

export const entityTypeLabel = (value?: string | null): string => {
  if (!value) return "";
  const match =
    ENTITY_TYPE_OPTIONS.find((o) => o.value === value) ??
    NOTIFICATION_ENTITY_TYPE_OPTIONS.find((o) => o.value === value) ??
    SCHEDULER_ENTITY_TYPE_OPTIONS.find((o) => o.value === value) ??
    DEFAULTS_ENTITY_TYPE_OPTIONS.find((o) => o.value === value);

  return match?.label ?? value;
};

export const workflowActionLabel = (value?: string | null): string => {
  if (!value) return "";
  const match = WORKFLOW_ACTION_OPTIONS.find((o) => o.value === value);

  return match?.label ?? value;
};

export const isSupportedSubsystemPair = (subsystem?: string | null, entity_type?: string | null): boolean => {
  return getStatesForPair(subsystem, entity_type).length > 0;
};

export const isTransitionSubsystem = (subsystem?: string | null): boolean =>
  subsystem === SUBSYSTEM_TRANSITION;

export const isBlockTransitionAction = (action?: string | null): boolean =>
  action === ACTION_BLOCK_TRANSITION;

export const isNotificationSubsystem = (subsystem?: string | null): boolean =>
  subsystem === SUBSYSTEM_NOTIFICATION;

export const isSchedulerSubsystem = (subsystem?: string | null): boolean =>
  subsystem === SUBSYSTEM_SCHEDULER;

export const isDefaultsSubsystem = (subsystem?: string | null): boolean =>
  subsystem === SUBSYSTEM_DEFAULTS;

// NOTIFICATION subsystem payload shapes — mirror
// @spendable-tech/workflow-primitives/subsystems/notification/schemas.ts.

export interface NotificationScopeShape {
  object_types: string[];
  organisation_id?: string;
}

export interface NotificationAmountRange {
  gte?: number;
  lte?: number;
}

// HH:mm 24-hour bounds. `[start, end)` — a start later than the end wraps past
// midnight. Evaluated in Australia/Sydney by notify-service.
export interface NotificationTimeOfDayShape {
  start: string;
  end: string;
}

export interface NotificationConditionShape {
  ops?: string[];
  from_status?: string[];
  to_status?: string[];
  decline_reason?: string[];
  amount?: NotificationAmountRange;
  // Category strings from UI_CONFIG mcc_category item ids — the same values
  // wallet preferences store in blocked_mcc_categories.
  mcc_categories?: string[];
  balance_after?: NotificationAmountRange;
  time_of_day?: NotificationTimeOfDayShape;
  days_of_week?: string[];
}

// TRANSACTION-only condition presets. Match the seven Novatti decline codes
// notify-service reads from `event.payload.decline_reason`.
export const NOTIFICATION_DECLINE_REASON_OPTIONS = [
  "Card not found",
  "Card is not active",
  "Card setting is invalid",
  "Wallet not found or merchant restricted",
  "Wallet is inactive",
  "Transaction limit exceeded",
  "Insufficient funds"
] as const;

// Codes mirror the backend enum; every surface shows the label (Q16).
export const NOTIFICATION_DAY_OF_WEEK_OPTIONS = [
  { value: "MON", label: "Monday" },
  { value: "TUE", label: "Tuesday" },
  { value: "WED", label: "Wednesday" },
  { value: "THU", label: "Thursday" },
  { value: "FRI", label: "Friday" },
  { value: "SAT", label: "Saturday" },
  { value: "SUN", label: "Sunday" }
] as const;

const DAY_OF_WEEK_LABELS = new Map<string, string>(
  NOTIFICATION_DAY_OF_WEEK_OPTIONS.map((o) => [o.value, o.label])
);

export const notificationDayLabel = (value?: string | null): string =>
  value ? DAY_OF_WEEK_LABELS.get(value) ?? value : "";

// Mirrors the backend HH:mm 24-hour regex on notification time_of_day.
const TIME_OF_DAY_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

export const isValidTimeOfDay = (value: string): boolean => TIME_OF_DAY_PATTERN.test(value);

export const timeOfDayLabel = (window?: NotificationTimeOfDayShape | null): string =>
  window?.start && window?.end ? `${window.start}–${window.end}` : "";

export type RecipientResolverShape =
  | { type: "role_based"; roles: string[]; scope: "entity_org" | "org"; wallet_scoped?: boolean }
  | { type: "relationship_based"; relationship_type: string; role?: string; from_object_type: "WALLET" | "USER" | "ORGANISATION" }
  | { type: "entity_derived"; field_path: string }
  | { type: "static"; user_ids: string[] };

// Entities whose notify-service snapshot reliably carries a wallet_id for the
// role_based wallet_scoped post-filter. Elsewhere the resolver fails closed
// (missing wallet_id resolves to nobody), so the checkbox is hidden for fresh
// picks on unsupported entities.
export const WALLET_SCOPED_SUPPORTED_ENTITIES = [ENTITY_TYPE_WALLET, ENTITY_TYPE_ACCOUNTING] as const;

export const isWalletScopedSupportedEntity = (entityType?: string | null): boolean =>
  (WALLET_SCOPED_SUPPORTED_ENTITIES as readonly string[]).includes(entityType ?? "");

// Curated `field_path` options exposed in the entity_derived recipient picker.
// The chosen preset's `field_path` is stored verbatim into the payload — API
// contract is unchanged. Extend as new producers enrich their snapshots.
// `phrase` names the recipient in the rule sentence.
export interface PresetFieldPath {
  label: string;
  field_path: string;
  phrase: string;
  hint?: string;
}

const WHO_SPENT_RECIPIENT = "the person who spent the money";
const CARDHOLDER_RECIPIENT = "the cardholder";

export const ENTITY_DERIVED_FIELD_PATH_PRESETS: Record<string, PresetFieldPath[]> = {
  [ENTITY_TYPE_ACCOUNTING]: [
    { label: "Transaction done by (cardholder)", field_path: "transacted_by", phrase: WHO_SPENT_RECIPIENT },
    { label: "Last updated by", field_path: "updated_by", phrase: "the person who last updated it" },
    { label: "Created by", field_path: "created_by", phrase: "the person who created it" },
    {
      label: "Rejected by",
      field_path: "rejection_context.rejected_by",
      phrase: "the person who rejected it",
      hint: "Only set once the record has been rejected, so no one is emailed when the rule fires on other changes."
    },
    { label: "Wallet approvers", field_path: "approvers", phrase: "the wallet's approvers" },
    { label: "Wallet verifiers", field_path: "verifiers", phrase: "the wallet's verifiers" }
  ],
  [ENTITY_TYPE_CARD]: [
    { label: CONDITION_FIELD_LABELS.CARDHOLDER, field_path: "user_id", phrase: CARDHOLDER_RECIPIENT }
  ],
  [ENTITY_TYPE_TRANSACTION]: [
    { label: "Cardholder", field_path: "user_id", phrase: CARDHOLDER_RECIPIENT },
    {
      label: "Person who made the transaction",
      field_path: "transaction_user_id",
      phrase: WHO_SPENT_RECIPIENT,
      hint: "On shared cards, the person who actually spent. No recipients when the transaction has no assigned spender."
    }
  ],
  [ENTITY_TYPE_REIMBURSEMENT]: [
    { label: "Person who requested", field_path: "requested_by", phrase: "the person who submitted the claim" }
  ],
  [ENTITY_TYPE_WALLET]: [
    {
      label: "Wallet owner",
      field_path: "owner_id",
      phrase: "the person the wallet belongs to",
      hint: "Emails the person the wallet belongs to. Group and organisation wallets have no single owner, so no one is emailed for them."
    }
  ]
};

export const getEntityDerivedFieldPathPresets = (entityType?: string | null): PresetFieldPath[] =>
  ENTITY_DERIVED_FIELD_PATH_PRESETS[entityType ?? ""] ?? [];

// Who a From entity recipient emails, in words; a path outside the presets stays unnamed.
const ENTITY_DERIVED_CUSTOM_RECIPIENT = "the person named on the record";

export const entityDerivedRecipientPhrase = (entityType: string | null | undefined, fieldPath: string): string =>
  getEntityDerivedFieldPathPresets(entityType).find((p) => p.field_path === fieldPath)?.phrase ??
  ENTITY_DERIVED_CUSTOM_RECIPIENT;

// Mirrors the backend Zod union in @spendable-tech/workflow-primitives 2.4.3:
// legacy single-resolver shape OR multi-resolver { resolvers: [...] }. N=1
// stays legacy on submit for backward-compat with existing rules.
export type NotificationRecipientsShape =
  | RecipientResolverShape
  | { resolvers: RecipientResolverShape[] };

// Unwraps either recipients shape to a flat resolver list.
export const resolversOf = (recipients?: NotificationRecipientsShape | null): RecipientResolverShape[] => {
  if (!recipients || typeof recipients !== "object") return [];
  if ("resolvers" in recipients) return Array.isArray(recipients.resolvers) ? recipients.resolvers : [];
  return [recipients];
};

export interface NotificationActionParamsShape {
  template: string;
  recipients: NotificationRecipientsShape;
  variables?: Record<string, string>;
}

// SCHEDULER subsystem payload shapes — hand-maintained alongside
// @spendable-tech/workflow-primitives/subsystems/scheduler/schemas.ts.
// Recipients reuse the NOTIFICATION resolver union verbatim.

export interface SchedulerScopeShape {
  object_types: string[];
  organisation_id?: string;
}

// The fire-once window. Both bounds are required — an open-ended window would
// re-match the same record on every cron run (no server-side idempotency state).
export interface SchedulerAgeHoursShape {
  min: number;
  max: number;
}

// Pure UTC ISO datetimes representing Australia/Sydney calendar-day bounds —
// FE serializes start-of-day (from) / end-of-day (to) Sydney; `to` omitted =
// up to now. Mirrors workflow-primitives 3.7.1.
export interface SchedulerCreatedBetweenShape {
  from: string;
  to?: string;
}

export const SCHEDULER_FREQUENCY_DAY_OF_WEEK_OPTIONS = [
  { value: "MONDAY", label: "Monday" },
  { value: "TUESDAY", label: "Tuesday" },
  { value: "WEDNESDAY", label: "Wednesday" },
  { value: "THURSDAY", label: "Thursday" },
  { value: "FRIDAY", label: "Friday" },
  { value: "SATURDAY", label: "Saturday" },
  { value: "SUNDAY", label: "Sunday" }
] as const;

export type SchedulerFrequencyDayOfWeek =
  (typeof SCHEDULER_FREQUENCY_DAY_OF_WEEK_OPTIONS)[number]["value"];

// Per-rule notification cadence — mirrors workflow-primitives 3.8.0. Absent =
// DAILY. Flat rules carry it in condition.frequency, only on the re-firing
// modes (schema rejects it alongside age_hours); tree rules carry it as the
// top-level `frequency`, never beside a bounded (Once) age window. `anchor` is a plain Australia/Sydney calendar day —
// stored verbatim, no UTC conversion.
export type SchedulerFrequencyShape =
  | { type: "DAILY" }
  | { type: "WEEKLY"; day_of_week: SchedulerFrequencyDayOfWeek }
  | { type: "EVERY_N_DAYS"; n: number; anchor: string };

// Fortnightly is presented as its own option in the builder but stored as
// EVERY_N_DAYS n=14; n===14 hydrates back to the Fortnightly option.
export const SCHEDULER_FORTNIGHT_DAYS = 14;

// Exactly one of age_hours / created_between per rule (backend XOR-refined,
// .strict()).
export interface SchedulerConditionShape {
  age_hours?: SchedulerAgeHoursShape;
  created_between?: SchedulerCreatedBetweenShape;
  frequency?: SchedulerFrequencyShape;
  statuses?: string[];
  has_receipt?: boolean;
}

// CARD wire shape — mirrors schedulerCardConditionSchema (.strict()) in
// @spendable-tech/workflow-primitives 3.8.0. A threshold, not a window:
// matches while 0 < days_until_expiry <= threshold, at the rule's cadence.
export interface SchedulerCardConditionShape {
  expires_within_days: number;
  frequency?: SchedulerFrequencyShape;
}

// CARD_SETTING wire shape — mirrors schedulerCardSettingConditionSchema
// (.strict()) in @spendable-tech/workflow-primitives 3.10.0. Every field is
// optional and AND-combined; each any-of/none-of pair is refined
// non-overlapping; empty arrays are rejected, so empties are omitted. An
// empty {} condition is legal by design = every active card setting in the
// rule's org. card_setting_types maps onto the stored card_setting_status
// field (PERMANENT|TRANSIENT|ONCE), not the row's ACTIVE/INACTIVE status.
export interface SchedulerCardSettingConditionShape {
  wallet_ids?: string[];
  wallet_ids_exclude?: string[];
  card_ids?: string[];
  card_ids_exclude?: string[];
  wallet_types?: string[];
  card_types?: string[];
  card_statuses?: string[];
  card_setting_types?: string[];
  frequency?: SchedulerFrequencyShape;
}

export const SCHEDULER_WALLET_TYPE_OPTIONS = Object.keys(walletTypeMapping);
export const SCHEDULER_CARD_TYPE_OPTIONS = Object.keys(cardTypeMapping);

// Stored card.card_status values — subset of SpendableCardStatus
// (@spendable-tech/dynamodb card.ts). CLOSED / DEACTIVATED / NOT_ACTIVE exist
// in the type but aren't offered (no UI labels, edge lifecycle states).
export const SCHEDULER_CARD_STATUS_OPTIONS = ["ACTIVE", "BLOCKED", "DRAFT", "HOLD", "ORDERED", "CANCELED"];

// card_setting_status vocabulary on CardSettingDBItem.
export const CARD_SETTING_TYPE_OPTIONS = ["PERMANENT", "TRANSIENT", "ONCE"];

export const cardSettingTypeMapping: Record<string, string> = {
  PERMANENT: "Permanent",
  TRANSIENT: "Temporary",
  ONCE: "One-time"
};

export const walletTypeLabel = (value: string): string => walletTypeMapping[value] ?? value;
export const cardTypeLabel = (value: string): string => cardTypeMapping[value] ?? value;
export const cardStatusLabel = (value: string): string => cardStatusMapping[value] ?? value;
export const cardSettingTypeLabel = (value: string): string => cardSettingTypeMapping[value] ?? value;

// Shared cadence label for list/detail — "Daily", "Weekly (Mon)",
// "Fortnightly from 27 Aug 2026", "Every 2 days from 27 Aug 2026".
export const schedulerFrequencyLabel = (freq?: SchedulerFrequencyShape | null): string => {
  if (!freq || freq.type === "DAILY") return "Daily";
  if (freq.type === "WEEKLY") {
    const day = freq.day_of_week.charAt(0) + freq.day_of_week.slice(1, 3).toLowerCase();

    return `Weekly (${day})`;
  }
  const from = formatSydneyDay(freq.anchor);

  return freq.n === SCHEDULER_FORTNIGHT_DAYS ? `Fortnightly from ${from}` : `Every ${freq.n} days from ${from}`;
};

// User-facing units for the record-age window. No Months — month-sized
// lookbacks belong to the exact-dates mode.
export type SchedulerAgeUnit = "HOURS" | "DAYS" | "WEEKS";

export const SCHEDULER_AGE_UNIT_HOURS: Record<SchedulerAgeUnit, number> = {
  HOURS: 1,
  DAYS: 24,
  WEEKS: 168
};

// The rule's run cadence in hours. Absent frequency = the daily cron, which
// is all the AGE_WINDOW schema permits today (it rejects frequency alongside
// age_hours) — the WEEKLY / EVERY_N_DAYS branches keep the derivation correct
// if cadence ever unlocks for age rules.
export const schedulerCadenceHours = (freq?: SchedulerFrequencyShape | null): number => {
  if (!freq || freq.type === "DAILY") return SCHEDULER_AGE_UNIT_HOURS.DAYS;
  if (freq.type === "WEEKLY") return SCHEDULER_AGE_UNIT_HOURS.WEEKS;
  return freq.n * SCHEDULER_AGE_UNIT_HOURS.DAYS;
};

// A tree rule's stored top-level frequency (AWSJSON); null = absent (DAILY).
export const parseTreeSchedulerFrequency = (raw: string | null | undefined): SchedulerFrequencyShape | null => {
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as SchedulerFrequencyShape | null;

    return value && typeof value === "object" && typeof value.type === "string" ? value : null;
  } catch {
    return null;
  }
};

// min = value in hours; max = min + one cadence, so each record sits in the
// window on exactly one run.
export const deriveSchedulerAgeWindow = (
  value: number,
  unit: SchedulerAgeUnit,
  freq?: SchedulerFrequencyShape | null
): SchedulerAgeHoursShape => {
  const min = value * SCHEDULER_AGE_UNIT_HOURS[unit];

  return { min, max: min + schedulerCadenceHours(freq) };
};

export interface SchedulerFriendlyAge {
  value: number;
  unit: SchedulerAgeUnit;
}

// Friendliest unit for a stored window — only when max sits exactly on the
// cadence-derived bound. Anything else is a custom window the builder must
// surface as raw hours, never silently rewrite.
export const schedulerAgeWindowToFriendly = (
  age?: Partial<SchedulerAgeHoursShape> | null,
  freq?: SchedulerFrequencyShape | null
): SchedulerFriendlyAge | undefined => {
  if (typeof age?.min !== "number" || typeof age?.max !== "number") return undefined;
  if (age.max !== age.min + schedulerCadenceHours(freq)) return undefined;
  if (age.min === 0) return { value: 0, unit: "HOURS" };
  if (age.min % SCHEDULER_AGE_UNIT_HOURS.WEEKS === 0) {
    return { value: age.min / SCHEDULER_AGE_UNIT_HOURS.WEEKS, unit: "WEEKS" };
  }
  if (age.min % SCHEDULER_AGE_UNIT_HOURS.DAYS === 0) {
    return { value: age.min / SCHEDULER_AGE_UNIT_HOURS.DAYS, unit: "DAYS" };
  }
  return { value: age.min, unit: "HOURS" };
};

const SCHEDULER_AGE_UNIT_NOUNS: Record<SchedulerAgeUnit, string> = {
  HOURS: "hour",
  DAYS: "day",
  WEEKS: "week"
};

// "1 week", "3 days", "0 hours" — shared by preview, detail and list.
export const schedulerFriendlyAgeLabel = (friendly: SchedulerFriendlyAge): string => {
  const noun = SCHEDULER_AGE_UNIT_NOUNS[friendly.unit];

  return `${friendly.value} ${noun}${friendly.value === 1 ? "" : "s"}`;
};

export interface SchedulerActionParamsShape {
  template: string;
  recipients: NotificationRecipientsShape;
}

// Fan-out outcomes written by report-service scheduledWorkflowRunProcessor
// (run level and per-entity entity_runs).
export const SCHEDULER_RUN_OUTCOME_LABELS: Record<string, string> = {
  DISPATCHED: "Notifications dispatched",
  NO_CANDIDATES: "No matching records",
  NO_RULES_DUE: "No rules due",
  NO_ACTIVE_RULES: "No active rules",
  RULE_PARSE_FAILED: "Rule could not be read"
};

export const schedulerRunOutcomeLabel = (value?: string | null): string => {
  if (!value) return "";
  return SCHEDULER_RUN_OUTCOME_LABELS[value] ?? value;
};

// DEFAULTS subsystem payload shapes — mirror
// @spendable-tech/workflow-primitives/subsystems/defaults/schemas.ts (3.3.0).
// PRD: /Users/vishalkhurana/Documents/spendable/allocation-defaults.md.

// Backend priority default is 1000; scope = { organisation_id, priority, wallet_ids }.
// wallet_ids is required and non-empty (Zod refine); FE-enforced to GROUP-owned
// wallets in the org via the wallet picker.
export interface AllocationDefaultsScopeShape {
  organisation_id: string;
  priority: number;
  wallet_ids: string[];
}

// Canonical transaction type keys — mirrored from AllocationTransactionTypeKey
// in @spendable-tech/dynamodb (kept in lockstep with the backend enum).
export const ALLOCATION_DEFAULTS_TRANSACTION_TYPE_KEYS = [
  "NOVATTI_CARD_TRANSACTION",
  "CASH_TRANSACTION",
  "ADJUSTMENT",
  "DEPOSIT_WITHDRAWAL",
  "WALLET_TO_WALLET_TRANSFER"
] as const;

export type AllocationDefaultsTransactionTypeKey =
  (typeof ALLOCATION_DEFAULTS_TRANSACTION_TYPE_KEYS)[number];

// Human labels for the 5 canonical values — backend uses raw enum on the wire.
export const ALLOCATION_DEFAULTS_TRANSACTION_TYPE_LABELS: Record<
  AllocationDefaultsTransactionTypeKey,
  string
> = {
  NOVATTI_CARD_TRANSACTION: "Card transaction",
  CASH_TRANSACTION: "Cash transaction",
  ADJUSTMENT: "Adjustment",
  DEPOSIT_WITHDRAWAL: "Deposit / Withdrawal",
  WALLET_TO_WALLET_TRANSFER: "Wallet-to-wallet transfer"
};

// User-facing dropdown options — narrower than the full 5-value enum.
// Auto-allocation only fires on NOVATTI_CARD_TRANSACTION today; CASH_TRANSACTION
// is future-facing under the same code path. The other 3 stay in the type +
// label map so historical rules created by other means still parse + render.
export const ALLOCATION_DEFAULTS_TRANSACTION_TYPE_OPTIONS = [
  "NOVATTI_CARD_TRANSACTION",
  "CASH_TRANSACTION"
] as const satisfies readonly AllocationDefaultsTransactionTypeKey[];

export const transactionTypeKeyLabel = (
  key?: AllocationDefaultsTransactionTypeKey | string | null
): string => {
  if (!key) return "";
  if (key in ALLOCATION_DEFAULTS_TRANSACTION_TYPE_LABELS) {
    return ALLOCATION_DEFAULTS_TRANSACTION_TYPE_LABELS[key as AllocationDefaultsTransactionTypeKey];
  }
  return String(key);
};

// Condition (wire shape) — AND semantics, each clause optional but at least
// one required (backend refine). Mirrors conditionSchema exactly: string OR
// string[] on `value` for `in`-capable clauses.
export interface AllocationDefaultsConditionShape {
  merchant_name?: {
    op: "contains" | "equals";
    value: string;
    case_insensitive?: boolean;
  };
  mcc?: {
    op: "equals" | "in";
    value: string | string[];
  };
  transaction_type_key?: {
    op: "equals" | "in";
    value: AllocationDefaultsTransactionTypeKey | AllocationDefaultsTransactionTypeKey[];
  };
  cardholder_user_id?: {
    op: "equals" | "in";
    value: string | string[];
  };
}

// Action (wire shape) — collapsed onto the action string itself
// (ACTION_EQUAL_PARTICIPANT_SPLIT). Backend Zod is z.object({}); runtime
// splits the parent amount equally across the scope wallet's group PARTICIPANTS.
// New variants (weighted, least-loaded, …) land as new action strings with
// their own action_params schema — mirrors the TRANSITION pattern.
export type AllocationDefaultsActionParamsShape = Record<string, never>;

// ─── FORM (in-memory) shapes ────────────────────────────────────────────────
// Kept normalised (arrays for every list-ish slot) so the picker chrome stays
// uniform; the round-trip helpers collapse to string vs string[] at submit.

export interface AllocationDefaultsMerchantNameClause {
  op: "contains" | "equals";
  value: string;
  case_insensitive?: boolean;
}

export interface AllocationDefaultsMccClause {
  op: "equals" | "in";
  value: string[];
}

export interface AllocationDefaultsTransactionTypeClause {
  op: "equals" | "in";
  value: AllocationDefaultsTransactionTypeKey[];
}

// user_ids + hydrated User[] snapshot so UserAutocomplete can render tags
// without a refetch — same trick RecipientResolverPicker uses.
export interface AllocationDefaultsCardholderClause {
  op: "equals" | "in";
  user_ids: string[];
  users: import("src/types/commonTypes").User[];
}

export interface AllocationDefaultsPredicateForm {
  merchant_name?: AllocationDefaultsMerchantNameClause;
  mcc?: AllocationDefaultsMccClause;
  transaction_type_key?: AllocationDefaultsTransactionTypeClause;
  cardholder_user_id?: AllocationDefaultsCardholderClause;
}

// ─── Trigger registry (Q1+Q2) — the create wizard's single plain-English
// trigger picker. Hand-written FE mapping derived strictly from the entity
// configs above (NOTIFICATION / TRANSITION / SCHEDULER / DEFAULTS) — only
// triggers the engine can run today. Picking one derives subsystem,
// entity_type, action and ops; the stored rule shape is exactly what the old
// Rule type + Applies to + Ops form produced.

export interface WorkflowTriggerOption {
  trigger_key: string;
  label: string;
  // One-line "what happens" shown under the label in the picker.
  hint: string;
  subsystem: string;
  entity_type: string;
  action: string;
  // Membership set stored into the rule's ops condition. Absent = the form
  // stores no ops key (the old form's untouched default — TRANSACTION, whose
  // only op is AUTHORIZE, and the eventless subsystems).
  ops?: readonly string[];
  // Outcome-driven entities (TRANSACTION): the to_status outcome this trigger
  // pins; the template derives from it (Q13).
  outcome?: string;
  // Offers the "kinds of movement" narrowing condition — stored ops may be
  // any non-empty subset of this trigger's set and still hydrate back to it.
  narrowable?: boolean;
}

export const WORKFLOW_TRIGGER_OPTIONS: WorkflowTriggerOption[] = [
  {
    trigger_key: "ACCOUNTING_BLOCK_STATUS_CHANGE",
    label: "A record is about to change status",
    hint: "Step in and block the change unless this rule's conditions are met.",
    subsystem: SUBSYSTEM_TRANSITION,
    entity_type: ENTITY_TYPE_ACCOUNTING,
    action: ACTION_BLOCK_TRANSITION
  },
  {
    trigger_key: "ACCOUNTING_AUTO_STATUS_CHANGE",
    label: "A record changes status",
    hint: "Move it on to the next status automatically.",
    subsystem: SUBSYSTEM_TRANSITION,
    entity_type: ENTITY_TYPE_ACCOUNTING,
    action: ACTION_AUTO_TRANSITION
  },
  {
    trigger_key: "ACCOUNTING_RECORD_CREATED",
    label: "A record is created in draft",
    hint: "Tell someone a receipt or action is needed on the new record.",
    subsystem: SUBSYSTEM_NOTIFICATION,
    entity_type: ENTITY_TYPE_ACCOUNTING,
    action: ACTION_SEND_NOTIFICATION,
    ops: ["CREATE"]
  },
  {
    trigger_key: "ACCOUNTING_RECORD_SUBMITTED",
    label: "A record is submitted for approval",
    hint: "Tell the approvers it's waiting on them.",
    subsystem: SUBSYSTEM_NOTIFICATION,
    entity_type: ENTITY_TYPE_ACCOUNTING,
    action: ACTION_SEND_NOTIFICATION,
    ops: ["SUBMIT"]
  },
  {
    trigger_key: "ACCOUNTING_RECORD_STATUS_CHANGED",
    label: "A record moves to a new status",
    hint: "Tell someone whenever the record's status changes.",
    subsystem: SUBSYSTEM_NOTIFICATION,
    entity_type: ENTITY_TYPE_ACCOUNTING,
    action: ACTION_SEND_NOTIFICATION,
    ops: NOTIFICATION_ENTITY_CONFIG[ENTITY_TYPE_ACCOUNTING].ops
  },
  {
    trigger_key: "ACCOUNTING_RECORDS_SIT_TOO_LONG",
    label: "Transaction records sit too long",
    hint: "Daily check for records waiting without action — e.g. a missing receipt.",
    subsystem: SUBSYSTEM_SCHEDULER,
    entity_type: ENTITY_TYPE_ACCOUNTING,
    action: ACTION_SEND_NOTIFICATION
  },
  {
    trigger_key: "CLAIM_SUBMITTED",
    label: "A claim is submitted",
    hint: "A reimbursement claim is sent in, or sent back in after changes.",
    subsystem: SUBSYSTEM_NOTIFICATION,
    entity_type: ENTITY_TYPE_REIMBURSEMENT,
    action: ACTION_SEND_NOTIFICATION,
    ops: ["SUBMIT", "RESUBMIT"]
  },
  {
    trigger_key: "CLAIM_DECIDED",
    label: "A claim is decided",
    hint: "A claim is approved or rejected.",
    subsystem: SUBSYSTEM_NOTIFICATION,
    entity_type: ENTITY_TYPE_REIMBURSEMENT,
    action: ACTION_SEND_NOTIFICATION,
    ops: ["APPROVE", "REJECT"]
  },
  {
    trigger_key: "CLAIM_SETTLED",
    label: "A claim is settled",
    hint: "The approved claim has been paid out.",
    subsystem: SUBSYSTEM_NOTIFICATION,
    entity_type: ENTITY_TYPE_REIMBURSEMENT,
    action: ACTION_SEND_NOTIFICATION,
    ops: ["SETTLE"]
  },
  {
    trigger_key: "CLAIM_CHANGED",
    label: "Anything happens to a claim",
    hint: "Any step in the claim's lifecycle, from update to settlement.",
    subsystem: SUBSYSTEM_NOTIFICATION,
    entity_type: ENTITY_TYPE_REIMBURSEMENT,
    action: ACTION_SEND_NOTIFICATION,
    ops: NOTIFICATION_ENTITY_CONFIG[ENTITY_TYPE_REIMBURSEMENT].ops
  },
  {
    trigger_key: "CARD_PAYMENT_MADE",
    label: "A payment is made",
    hint: "A card payment goes through.",
    subsystem: SUBSYSTEM_NOTIFICATION,
    entity_type: ENTITY_TYPE_TRANSACTION,
    action: ACTION_SEND_NOTIFICATION,
    outcome: "APPROVED"
  },
  {
    trigger_key: "CARD_PAYMENT_DECLINED",
    label: "A payment is declined",
    hint: "A card payment is refused at the terminal.",
    subsystem: SUBSYSTEM_NOTIFICATION,
    entity_type: ENTITY_TYPE_TRANSACTION,
    action: ACTION_SEND_NOTIFICATION,
    outcome: NOTIFICATION_OUTCOME_DECLINED
  },
  {
    trigger_key: "WALLET_MONEY_IN",
    label: "Money arrives in a wallet",
    hint: "A deposit or an incoming transfer.",
    subsystem: SUBSYSTEM_NOTIFICATION,
    entity_type: ENTITY_TYPE_WALLET,
    action: ACTION_SEND_NOTIFICATION,
    ops: ["DEPOSIT", "TRANSFER_IN"],
    narrowable: true
  },
  {
    trigger_key: "WALLET_MONEY_OUT",
    label: "Money leaves a wallet",
    hint: "A purchase, withdrawal, outgoing transfer or cash transaction.",
    subsystem: SUBSYSTEM_NOTIFICATION,
    entity_type: ENTITY_TYPE_WALLET,
    action: ACTION_SEND_NOTIFICATION,
    ops: ["TRANSFER_OUT", "PURCHASE", "WITHDRAWAL", "CASH"],
    narrowable: true
  },
  {
    trigger_key: "WALLET_MONEY_MOVES",
    label: "Money moves in a wallet",
    hint: "Any movement in or out, including adjustments.",
    subsystem: SUBSYSTEM_NOTIFICATION,
    entity_type: ENTITY_TYPE_WALLET,
    action: ACTION_SEND_NOTIFICATION,
    ops: NOTIFICATION_ENTITY_CONFIG[ENTITY_TYPE_WALLET].ops,
    narrowable: true
  },
  {
    trigger_key: "CARD_EXPIRING_SOON",
    label: "A card is close to expiring",
    hint: "Daily check that warns the owner before the card expires.",
    subsystem: SUBSYSTEM_SCHEDULER,
    entity_type: ENTITY_TYPE_CARD,
    action: ACTION_SEND_NOTIFICATION
  },
  {
    trigger_key: "CARD_CONNECTION_NIGHTLY_CHECK",
    label: "Nightly card-connection check",
    hint: "Switch off matching card connections on the nightly run.",
    subsystem: SUBSYSTEM_SCHEDULER,
    entity_type: ENTITY_TYPE_CARD_SETTING,
    action: ACTION_DEACTIVATE_CARD_SETTINGS
  },
  {
    trigger_key: "SPLIT_THE_COST",
    label: "Split the cost",
    hint: "Split matching transactions equally across the group's participants.",
    subsystem: SUBSYSTEM_DEFAULTS,
    entity_type: ENTITY_TYPE_ALLOCATION,
    action: ACTION_EQUAL_PARTICIPANT_SPLIT
  }
];

export const findWorkflowTrigger = (triggerKey?: string | null): WorkflowTriggerOption | undefined =>
  triggerKey ? WORKFLOW_TRIGGER_OPTIONS.find((t) => t.trigger_key === triggerKey) : undefined;

// Picker grouping — "what it's about", keyed on the existing entity labels.
export interface WorkflowTriggerGroup {
  entity_type: string;
  label: string;
  triggers: WorkflowTriggerOption[];
}

export const WORKFLOW_TRIGGER_GROUPS: WorkflowTriggerGroup[] = WORKFLOW_TRIGGER_OPTIONS.reduce<
  WorkflowTriggerGroup[]
>((groups, trigger) => {
  const existing = groups.find((g) => g.entity_type === trigger.entity_type);

  if (existing) existing.triggers.push(trigger);
  else {
    groups.push({
      entity_type: trigger.entity_type,
      label: entityTypeLabel(trigger.entity_type),
      triggers: [trigger]
    });
  }
  return groups;
}, []);

// The ops set a trigger effectively fires on — absent ops fall back to every
// op the entity registers (only meaningful for NOTIFICATION entities).
export const triggerEffectiveOps = (trigger: WorkflowTriggerOption): string[] => {
  if (!isNotificationSubsystem(trigger.subsystem)) return [];
  return trigger.ops ? [...trigger.ops] : [...getNotificationEntityConfig(trigger.entity_type).ops];
};

const sameOpsSet = (a: readonly string[], b: readonly string[]): boolean =>
  a.length === b.length && a.every((v) => b.includes(v));

export interface StoredTriggerShape {
  subsystem?: string | null;
  entity_type?: string | null;
  action?: string | null;
  ops: string[];
  to_status: string[];
}

// Hydrates a stored rule back to its trigger. Ops compare as sets, with an
// absent/empty stored set normalised to the entity's full op vocabulary (the
// two are equivalent on the wire — no ops key means "any op"). A non-empty
// proper subset hydrates to the smallest narrowable trigger containing it
// (the shape the movement-kind narrowing produces). Anything else — custom
// ops sets, internal-authored rules — returns undefined and the form falls
// back to the legacy fields. Matching never rewrites stored values.
export const findTriggerForStoredRule = ({
  subsystem,
  entity_type,
  action,
  ops,
  to_status
}: StoredTriggerShape): WorkflowTriggerOption | undefined => {
  const candidates = WORKFLOW_TRIGGER_OPTIONS.filter(
    (t) => t.subsystem === subsystem && t.entity_type === entity_type && t.action === action
  );

  if (candidates.length === 0) return undefined;
  if (!isNotificationSubsystem(subsystem)) return candidates[0];

  const configOps = getNotificationEntityConfig(entity_type).ops;
  const storedOps = ops.length ? ops : configOps;
  let subsetMatch: WorkflowTriggerOption | undefined;

  for (const candidate of candidates) {
    if (candidate.outcome && !(to_status.length === 1 && to_status[0] === candidate.outcome)) continue;
    const candidateOps = triggerEffectiveOps(candidate);

    if (sameOpsSet(storedOps, candidateOps)) return candidate;
    if (
      candidate.narrowable &&
      storedOps.length < candidateOps.length &&
      storedOps.every((op) => candidateOps.includes(op))
    ) {
      if (!subsetMatch || candidateOps.length < triggerEffectiveOps(subsetMatch).length) {
        subsetMatch = candidate;
      }
    }
  }
  return subsetMatch;
};

// ─── Condition-tree label layer (Q5). Wire shape + per-entity vocabularies
// live in conditionTreeBuilder/conditionTreeModel; field labels, sentence
// phrasing, enum options and the plain-English renderer live here with the
// rest of the label layer.

export interface ConditionTreeFieldCopy {
  // Field picker label.
  label: string;
  // Sentence subject — "the amount moved".
  subject: string;
  valueLabel?: (_value: string) => string;
  // Leads the value list in sentences — "the movement is a deposit".
  valueArticle?: string;
  inputLabel?: string;
  placeholder?: string;
  helper?: string;
  // Boolean fields: the check for true / false ("is attached" / "is missing").
  checks?: readonly [string, string];
  // Values read as-is in sentences (names, reasons) instead of lowercased.
  keepCase?: boolean;
  // Values are MCC category ids, named through the caller's resolver.
  mccCategoryValues?: boolean;
}

// The noun the join line reads — "the record matches all of these".
const CONDITION_TREE_SUBJECT_NOUNS: Record<string, string> = {
  [TREE_KEY_NOTIFICATION_REIMBURSEMENT]: "claim",
  [TREE_KEY_NOTIFICATION_TRANSACTION]: "payment",
  [TREE_KEY_NOTIFICATION_WALLET]: "movement",
  [TREE_KEY_SCHEDULER_CARD]: "card",
  [TREE_KEY_SCHEDULER_CARD_SETTING]: "connection",
  [TREE_KEY_DEFAULTS_ALLOCATION]: "transaction"
};

export const conditionTreeSubjectNoun = (key: string): string => CONDITION_TREE_SUBJECT_NOUNS[key] ?? "record";

const CASE_SENSITIVE_HELPER = "Matching is case-sensitive — \"Uber\" won't match \"UBER\".";
const MCC_HELPER = "Comma-separate multiple codes.";

const MERCHANT_NAME_COPY: ConditionTreeFieldCopy = {
  label: CONDITION_FIELD_LABELS.MERCHANT_NAME,
  subject: "the merchant name",
  inputLabel: CONDITION_FIELD_LABELS.MERCHANT_NAME,
  placeholder: "e.g. Woolworths",
  helper: CASE_SENSITIVE_HELPER
};

const MCC_COPY: ConditionTreeFieldCopy = {
  label: CONDITION_FIELD_LABELS.MCC,
  subject: "the MCC",
  inputLabel: "MCC codes",
  placeholder: "e.g. 5812, 5814",
  helper: MCC_HELPER
};

const RECEIPT_COUNT_COPY: ConditionTreeFieldCopy = {
  label: CONDITION_FIELD_LABELS.RECEIPT_COUNT,
  subject: "the number of receipts",
  inputLabel: "Receipts"
};

const HAS_RECEIPT_COPY: ConditionTreeFieldCopy = {
  label: CONDITION_FIELD_LABELS.RECEIPT,
  subject: "the receipt",
  checks: CONDITION_FIELD_CHECKS.RECEIPT
};

const AMOUNT_COPY: ConditionTreeFieldCopy = { label: CONDITION_FIELD_LABELS.AMOUNT, subject: "the amount" };

const WALLET_COPY: ConditionTreeFieldCopy = {
  label: CONDITION_FIELD_LABELS.WALLET,
  subject: "the wallet",
  placeholder: "Choose wallets"
};

const WHO_SPENT_COPY: ConditionTreeFieldCopy = {
  label: CONDITION_FIELD_LABELS.WHO_SPENT,
  subject: "the person who spent",
  placeholder: "Choose people"
};

const CARDHOLDER_COPY: ConditionTreeFieldCopy = {
  label: CONDITION_FIELD_LABELS.CARDHOLDER,
  subject: "the cardholder",
  placeholder: "Choose people"
};

const statusCopy = (label: string, subject: string): ConditionTreeFieldCopy => ({
  label,
  subject,
  valueLabel: workflowStatusLabel,
  inputLabel: "Statuses",
  placeholder: "Pick statuses"
});

const enumCopy = (
  label: string,
  subject: string,
  valueLabel: (_value: string) => string,
  inputLabel: string
): ConditionTreeFieldCopy => ({ label, subject, valueLabel, inputLabel, placeholder: `Pick ${inputLabel.toLowerCase()}` });

const WALLET_TYPE_COPY = enumCopy(
  CONDITION_FIELD_LABELS.WALLET_TYPE,
  "the wallet type",
  walletTypeLabel,
  "Wallet types"
);
const CARD_TYPE_COPY = enumCopy(CONDITION_FIELD_LABELS.CARD_TYPE, "the card type", cardTypeLabel, "Card types");
const CARD_STATUS_COPY = enumCopy(
  CONDITION_FIELD_LABELS.CARD_STATUS,
  "the card status",
  cardStatusLabel,
  "Card statuses"
);
const BALANCE_AFTER_COPY: ConditionTreeFieldCopy = {
  label: CONDITION_FIELD_LABELS.BALANCE_AFTER,
  subject: "the balance afterwards"
};

const CONDITION_TREE_FIELD_COPY: Record<string, Record<string, ConditionTreeFieldCopy>> = {
  [TREE_KEY_TRANSITION_ACCOUNTING]: {
    activity_name: enumCopy(CONDITION_FIELD_LABELS.RECORD_TYPE, "the record type", activityNameLabel, "Record types"),
    wallet_id: WALLET_COPY,
    amount: AMOUNT_COPY,
    receipt_count: RECEIPT_COUNT_COPY,
    has_receipt: HAS_RECEIPT_COPY,
    // Rejection-reason notes don't count (transaction-service).
    has_note: { label: CONDITION_FIELD_LABELS.NOTE, subject: "the record", checks: CONDITION_FIELD_CHECKS.NOTE },
    has_approvers: {
      label: CONDITION_FIELD_LABELS.WALLET_APPROVERS,
      subject: "the wallet approvers",
      checks: CONDITION_FIELD_CHECKS.WALLET_APPROVERS
    },
    has_verifiers: {
      label: CONDITION_FIELD_LABELS.WALLET_VERIFIERS,
      subject: "the wallet verifiers",
      checks: CONDITION_FIELD_CHECKS.WALLET_VERIFIERS
    }
  },
  [TREE_KEY_NOTIFICATION_ACCOUNTING]: {
    op: enumCopy(CONDITION_FIELD_LABELS.WHAT_HAPPENED, "the record", workflowOpLabel, "Events"),
    from_status: statusCopy(CONDITION_FIELD_LABELS.FROM_STATUS, "the status before"),
    status: statusCopy(CONDITION_FIELD_LABELS.STATUS_AFTER, "the status afterwards"),
    wallet_id: WALLET_COPY,
    transacted_by: WHO_SPENT_COPY
  },
  [TREE_KEY_NOTIFICATION_REIMBURSEMENT]: {
    op: enumCopy(CONDITION_FIELD_LABELS.WHAT_HAPPENED, "the claim", workflowOpLabel, "Events"),
    from_status: statusCopy(CONDITION_FIELD_LABELS.FROM_STATUS, "the claim status before"),
    status: statusCopy(CONDITION_FIELD_LABELS.CLAIM_STATUS_AFTER, "the claim status afterwards"),
    amount: { label: CONDITION_FIELD_LABELS.CLAIM_AMOUNT, subject: "the claim amount" },
    source_wallet_id: { ...WALLET_COPY, label: CONDITION_FIELD_LABELS.PAID_FROM_WALLET, subject: "the paying wallet" },
    destination_wallet_id: {
      ...WALLET_COPY,
      label: CONDITION_FIELD_LABELS.PAID_INTO_WALLET,
      subject: "the receiving wallet"
    },
    requested_by: {
      ...WHO_SPENT_COPY,
      label: CONDITION_FIELD_LABELS.WHO_SUBMITTED_CLAIM,
      subject: "the person who submitted the claim"
    },
    merchant_name: MERCHANT_NAME_COPY
  },
  [TREE_KEY_NOTIFICATION_TRANSACTION]: {
    [TREE_FIELD_TO_STATUS]: enumCopy(
      CONDITION_FIELD_LABELS.PAYMENT_OUTCOME,
      "the payment",
      notificationOutcomeLabel,
      "Outcome"
    ),
    decline_reason: {
      label: CONDITION_FIELD_LABELS.DECLINE_REASON,
      subject: "the decline reason",
      inputLabel: "Reasons",
      placeholder: "Pick decline reasons",
      keepCase: true
    },
    mcc_category: {
      label: CONDITION_FIELD_LABELS.MERCHANT_CATEGORY,
      subject: "the merchant category",
      inputLabel: "Categories",
      placeholder: "Pick merchant categories",
      helper: "The same categories wallet settings use for merchant blocking.",
      mccCategoryValues: true
    },
    day_of_week: {
      ...enumCopy(CONDITION_FIELD_LABELS.DAY_OF_WEEK, "the day", notificationDayLabel, "Days"),
      keepCase: true
    },
    "transaction.amount": { label: CONDITION_FIELD_LABELS.PAYMENT_AMOUNT, subject: "the payment amount" },
    "transaction.balance_after_txn": BALANCE_AFTER_COPY,
    "transaction.merchantName": MERCHANT_NAME_COPY,
    mcc: MCC_COPY,
    "transaction.wallet_id": WALLET_COPY,
    user_id: CARDHOLDER_COPY,
    transaction_user_id: WHO_SPENT_COPY
  },
  [TREE_KEY_NOTIFICATION_WALLET]: {
    op: {
      label: CONDITION_FIELD_LABELS.MOVEMENT,
      subject: "the movement",
      valueLabel: workflowOpLabel,
      valueArticle: "a ",
      inputLabel: "Movements",
      placeholder: "Pick kinds of movement"
    },
    amount: { label: CONDITION_FIELD_LABELS.AMOUNT_MOVED, subject: "the amount moved" },
    balance_after_txn: BALANCE_AFTER_COPY,
    balance_before_txn: { label: CONDITION_FIELD_LABELS.BALANCE_BEFORE, subject: "the balance before" },
    wallet_id: WALLET_COPY,
    wallet_type: WALLET_TYPE_COPY
  },
  [TREE_KEY_SCHEDULER_ACCOUNTING]: {
    status: statusCopy(CONDITION_FIELD_LABELS.STATUS, "the status"),
    wallet_id: WALLET_COPY,
    user_id: WHO_SPENT_COPY,
    merchant_name: MERCHANT_NAME_COPY,
    amount: AMOUNT_COPY,
    receipt_count: RECEIPT_COUNT_COPY,
    has_receipt: HAS_RECEIPT_COPY
  },
  [TREE_KEY_SCHEDULER_CARD]: {
    card_status: { ...CARD_STATUS_COPY, helper: "Only active, blocked and on-hold cards are checked." },
    card_type: CARD_TYPE_COPY,
    user_id: CARDHOLDER_COPY
  },
  [TREE_KEY_SCHEDULER_CARD_SETTING]: {
    wallet_id: WALLET_COPY,
    card_id: { label: CONDITION_FIELD_LABELS.CARD, subject: "the card", placeholder: "Choose cards" },
    wallet_type: WALLET_TYPE_COPY,
    card_type: CARD_TYPE_COPY,
    card_status: CARD_STATUS_COPY,
    card_setting_status: enumCopy(
      CONDITION_FIELD_LABELS.CONNECTION_TYPE,
      "the connection type",
      cardSettingTypeLabel,
      "Connection types"
    )
  },
  [TREE_KEY_DEFAULTS_ALLOCATION]: {
    merchant_name: MERCHANT_NAME_COPY,
    mcc: MCC_COPY,
    transaction_type_key: enumCopy(
      CONDITION_FIELD_LABELS.RECORD_TYPE,
      "the record type",
      transactionTypeKeyLabel,
      "Record types"
    ),
    cardholder_user_id: CARDHOLDER_COPY
  }
};

export const conditionTreeFieldCopy = (key: string, field?: string | null): ConditionTreeFieldCopy => {
  if (!field) return { label: "", subject: "" };
  return CONDITION_TREE_FIELD_COPY[key]?.[field] ?? { label: enumToPhrase(field), subject: enumToPhrase(field) };
};

const CONDITION_TREE_ENUM_OPTIONS: Record<string, Record<string, readonly string[]>> = {
  [TREE_KEY_TRANSITION_ACCOUNTING]: { activity_name: ACCOUNTING_ACTIVITY_NAMES },
  [TREE_KEY_NOTIFICATION_ACCOUNTING]: {
    op: NOTIFICATION_ENTITY_CONFIG[ENTITY_TYPE_ACCOUNTING].ops,
    from_status: NOTIFICATION_ENTITY_CONFIG[ENTITY_TYPE_ACCOUNTING].statuses,
    status: NOTIFICATION_ENTITY_CONFIG[ENTITY_TYPE_ACCOUNTING].statuses
  },
  [TREE_KEY_NOTIFICATION_REIMBURSEMENT]: {
    op: NOTIFICATION_ENTITY_CONFIG[ENTITY_TYPE_REIMBURSEMENT].ops,
    from_status: NOTIFICATION_ENTITY_CONFIG[ENTITY_TYPE_REIMBURSEMENT].statuses,
    status: NOTIFICATION_ENTITY_CONFIG[ENTITY_TYPE_REIMBURSEMENT].statuses
  },
  // mcc_category options come from the store (UI_CONFIG categories) — the
  // form passes them in.
  [TREE_KEY_NOTIFICATION_TRANSACTION]: {
    [TREE_FIELD_TO_STATUS]: NOTIFICATION_ENTITY_CONFIG[ENTITY_TYPE_TRANSACTION].statuses,
    decline_reason: NOTIFICATION_DECLINE_REASON_OPTIONS,
    day_of_week: NOTIFICATION_DAY_OF_WEEK_OPTIONS.map((o) => o.value)
  },
  [TREE_KEY_NOTIFICATION_WALLET]: {
    op: NOTIFICATION_ENTITY_CONFIG[ENTITY_TYPE_WALLET].ops,
    wallet_type: SCHEDULER_WALLET_TYPE_OPTIONS
  },
  [TREE_KEY_SCHEDULER_ACCOUNTING]: { status: SCHEDULER_ENTITY_CONFIG[ENTITY_TYPE_ACCOUNTING].statuses },
  // Only these statuses reach the card scheduler's evaluation.
  [TREE_KEY_SCHEDULER_CARD]: { card_status: ["ACTIVE", "BLOCKED", "HOLD"], card_type: SCHEDULER_CARD_TYPE_OPTIONS },
  [TREE_KEY_SCHEDULER_CARD_SETTING]: {
    wallet_type: SCHEDULER_WALLET_TYPE_OPTIONS,
    card_type: SCHEDULER_CARD_TYPE_OPTIONS,
    card_status: SCHEDULER_CARD_STATUS_OPTIONS,
    card_setting_status: CARD_SETTING_TYPE_OPTIONS
  },
  [TREE_KEY_DEFAULTS_ALLOCATION]: { transaction_type_key: ALLOCATION_DEFAULTS_TRANSACTION_TYPE_OPTIONS }
};

export const conditionTreeEnumOptions = (key: string, field: string): string[] => [
  ...(CONDITION_TREE_ENUM_OPTIONS[key]?.[field] ?? [])
];

export const CONDITION_TREE_OP_LABELS: Record<ConditionTreeOp, string> = {
  eq: "is",
  neq: "is not",
  in: "is one of",
  not_in: "is not one of",
  gte: "is at least",
  lte: "is at most",
  contains: "contains",
  exists: "is set"
};

export const conditionTreeOpLabel = (op: ConditionTreeOp | ""): string =>
  op ? CONDITION_TREE_OP_LABELS[op] ?? op : "";

// A group's join: header option, its hint, and the connector read between rows.
export const CONDITION_TREE_JOIN_COPY: Record<
  ConditionTreeGroupType,
  { label: string; hint: string; connector: string }
> = {
  ALL: { label: "all of these", hint: "every one must be true", connector: "and" },
  ANY: { label: "any of these", hint: "one is enough", connector: "or" },
  NONE: { label: "none of these", hint: "all must be false", connector: "and not" }
};

export interface DescribeConditionTreeOptions {
  // Vocabulary the tree was authored in; defaults to WALLET notifications.
  vocabularyKey?: string;
  // Resolve ids to display names; unresolved ids fall back to a count phrase
  // so raw ids never reach a sentence.
  walletName?: (_id: string) => string | undefined;
  userName?: (_id: string) => string | undefined;
  cardName?: (_id: string) => string | undefined;
  mccCategoryName?: (_id: string) => string | undefined;
}

const treeJoin = (parts: string[], conjunction: string): string => {
  if (parts.length <= 1) return parts[0] ?? "";
  return `${parts.slice(0, -1).join(", ")} ${conjunction} ${parts[parts.length - 1]}`;
};

const treeMoney = (n: number): string => (Number.isInteger(n) ? `$${n}` : `$${n.toFixed(2)}`);

const ENTITY_NOUNS: Record<string, [string, string]> = {
  wallets: ["wallet", "wallets"],
  users: ["person", "people"],
  cards: ["card", "cards"]
};

export const namedEntityPhrase = (
  subject: string,
  kind: "wallets" | "users" | "cards",
  ids: string[],
  negated: boolean,
  resolve?: (_id: string) => string | undefined
): string => {
  const names = ids.map((id) => resolve?.(id)).filter((name): name is string => Boolean(name));
  const verb = negated ? "is not" : "is";
  const [singular, plural] = ENTITY_NOUNS[kind];

  if (names.length === ids.length && names.length > 0) return `${subject} ${verb} ${treeJoin(names, "or")}`;
  if (ids.length === 1) return `${subject} ${verb} the selected ${singular}`;
  return `${subject} ${verb} one of ${ids.length} selected ${plural}`;
};

export const describeTreeLeaf = (
  leaf: ConditionTreeLeafShape,
  key: string,
  opts: DescribeConditionTreeOptions
): string => {
  const values = Array.isArray(leaf.value) ? leaf.value : [leaf.value];
  const negated = leaf.op === "not_in" || leaf.op === "neq";
  const membership = leaf.op === "in" || leaf.op === "not_in" || leaf.op === "eq" || leaf.op === "neq";
  const def = conditionTreeFieldDef(key, leaf.field);
  const copy = conditionTreeFieldCopy(key, leaf.field);

  if (def) {
    switch (def.valueKind) {
      case "enum":
        if (membership && copy.mccCategoryValues) {
          const names = values.map((v) => opts.mccCategoryName?.(String(v))).filter((n): n is string => Boolean(n));
          const verb = negated ? "is not" : "is";

          if (names.length === values.length) return `${copy.subject} ${verb} ${treeJoin(names, "or")}`;
          if (values.length === 1) return `${copy.subject} ${verb} the selected category`;
          return `${copy.subject} ${verb} one of ${values.length} selected categories`;
        }
        if (membership) {
          const labels = values.map((v) => {
            const label = copy.valueLabel ? copy.valueLabel(String(v)) : String(v);

            return copy.keepCase ? label : label.toLowerCase();
          });

          return `${copy.subject} is ${negated ? "not " : ""}${copy.valueArticle ?? ""}${treeJoin(labels, "or")}`;
        }
        break;
      case "money":
        if (typeof leaf.value === "number") {
          const dollars = def.unit === "CENTS" ? leaf.value / 100 : leaf.value;

          return `${copy.subject} ${conditionTreeOpLabel(leaf.op)} ${treeMoney(dollars)}`;
        }
        break;
      case "count":
        if (typeof leaf.value === "number") return `${copy.subject} ${conditionTreeOpLabel(leaf.op)} ${leaf.value}`;
        break;
      case "boolean":
        if (typeof leaf.value === "boolean" && leaf.op === "eq" && copy.checks) {
          return `${copy.subject} ${conditionCheckLabel(copy.checks, leaf.value)}`;
        }
        break;
      case "wallets":
      case "users":
      case "cards": {
        const resolvers = { wallets: opts.walletName, users: opts.userName, cards: opts.cardName };

        if (membership) {
          return namedEntityPhrase(copy.subject, def.valueKind, values.map(String), negated, resolvers[def.valueKind]);
        }
        break;
      }
      case "text":
        if (typeof leaf.value === "string") {
          return `${copy.subject} ${conditionTreeOpLabel(leaf.op)} "${leaf.value}"`;
        }
        break;
      case "codes":
        if (membership) return `${copy.subject} is ${negated ? "not " : ""}${treeJoin(values.map(String), "or")}`;
        break;
    }
  }
  // Generic fallback — any schema-valid leaf outside the vocabulary still
  // reads as a sentence fragment rather than failing to render.
  if (leaf.op === "exists") return `${enumToPhrase(leaf.field)} ${leaf.value ? "is set" : "is not set"}`;
  return `${enumToPhrase(leaf.field)} ${conditionTreeOpLabel(leaf.op)} ${treeJoin(values.map(String), "or")}`;
};

const NONE_SET_LEAD = "none of these is true: ";

// A none-set reads bare as the last clause of an and/or join, in brackets anywhere else.
const bracketNoneSets = (parts: string[], inNoneSet: boolean): string[] =>
  parts.map((part, i) => (part.startsWith(NONE_SET_LEAD) && (inNoneSet || i < parts.length - 1) ? `(${part})` : part));

// A set reads in brackets with its own join; "none of these" lists its rows.
export const describeTreeNodes = (
  type: ConditionTreeGroupShape["type"],
  parts: string[],
  nested: boolean
): string => {
  if (type === "NONE") return `${NONE_SET_LEAD}${bracketNoneSets(parts, true).join(", ")}`;
  const joined = treeJoin(bracketNoneSets(parts, false), type === "ALL" ? "and" : "or");

  return nested && parts.length > 1 ? `(${joined})` : joined;
};

const describeTreeGroup = (
  group: ConditionTreeGroupShape,
  key: string,
  opts: DescribeConditionTreeOptions,
  nested: boolean
): string => {
  const [only] = group.conditions;

  // An ALL wrapper around one set (a pinned rule's any/none join) reads as that set.
  if (group.type === "ALL" && group.conditions.length === 1 && isConditionTreeGroupShape(only)) {
    return describeTreeGroup(only, key, opts, nested);
  }
  return describeTreeNodes(
    group.type,
    group.conditions.map((node: ConditionTreeNodeShape) =>
      isConditionTreeGroupShape(node) ? describeTreeGroup(node, key, opts, true) : describeTreeLeaf(node, key, opts)
    ),
    nested
  );
};

// Sydney minute of the day → "9:00 pm".
export const minuteOfDayLabel = (minute: number): string => {
  const wrapped = ((minute % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  const hour = Math.floor(wrapped / 60);
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;

  return `${hour12}:${String(wrapped % 60).padStart(2, "0")} ${hour >= 12 ? "pm" : "am"}`;
};

// "the time is between 9:00 am and 5:00 pm (Sydney)"; a window running past midnight reads as overnight.
export const timeOfDayPhrase = (from: number, to: number): string => {
  const window = `${minuteOfDayLabel(from)} and ${minuteOfDayLabel(to)} (Sydney)`;

  return from < to ? `the time is between ${window}` : `the time is overnight between ${window}`;
};

// The anchor (scheduler When input / time-of-day window) as a phrase — "the record is at least 3 days old".
const describeConditionTreeAnchor = (key: string, tree: ConditionTreeGroupShape): string | null => {
  const anchor = getConditionTreeVocabulary(key)?.anchor;
  const split = anchor ? splitConditionTreeAnchor(anchor, tree) : null;

  if (!split) return null;
  const { minute_from: from, minute_to: to } = split.values;

  if (typeof from === "number" && typeof to === "number") return timeOfDayPhrase(from, to);
  if (split.values.age_min_hours === undefined && split.values.expires_within_days === undefined) return null;
  if (typeof split.values.expires_within_days === "number") {
    return `the card expires within ${split.values.expires_within_days} day${split.values.expires_within_days === 1 ? "" : "s"}`;
  }
  const min = split.values.age_min_hours ?? 0;
  const friendly = schedulerAgeWindowToFriendly({ min, max: min + SCHEDULER_AGE_UNIT_HOURS.DAYS });

  return friendly ? `the record is at least ${schedulerFriendlyAgeLabel(friendly)} old` : null;
};

// Plain-English and/or/none-of sentence fragment for a stored condition tree —
// shared by the form preview, the detail page and the list summary. Scheduler
// anchors lead the sentence in their friendly form.
export const describeConditionTree = (
  tree: ConditionTreeGroupShape,
  opts: DescribeConditionTreeOptions = {}
): string => {
  const key = opts.vocabularyKey ?? TREE_KEY_NOTIFICATION_WALLET;
  const anchor = getConditionTreeVocabulary(key)?.anchor;
  const split = anchor ? splitConditionTreeAnchor(anchor, tree) : null;

  const anchorPhrase = split ? describeConditionTreeAnchor(key, tree) : null;

  if (!split || !anchorPhrase) return describeTreeGroup(tree, key, opts, false);
  const rest = split.rest.map((node) =>
    isConditionTreeGroupShape(node) ? describeTreeGroup(node, key, opts, true) : describeTreeLeaf(node, key, opts)
  );
  // Same slot the wire uses: right after the locked trigger leaf.
  const lockedField = getConditionTreeVocabulary(key)?.lockedField;
  const first = split.rest[0];
  const lead = first && !isConditionTreeGroupShape(first) && first.field === lockedField ? 1 : 0;

  return describeTreeNodes("ALL", [...rest.slice(0, lead), anchorPhrase, ...rest.slice(lead)], false);
};
